import { useId, type ReactElement } from 'react';
import { Calendar, FileSignature } from 'lucide-react';
import { Link } from 'react-router-dom';

import { OsVinculadasDoTermo } from '@/modulos/portal-credenciado/ordens-servico/OsVinculadasDoTermo';
import { StatusBadge } from '@/ui/StatusBadge';
import { Card, CardContent } from '@/ui/base/card';

import {
  ROTULO_CONVITE,
  ROTULO_STATUS_TERMO,
  VARIANTE_STATUS_TERMO,
  dataLegivel,
  numeroExibido,
  type MeuTermoResumo,
} from './contrato';

/** O que falta do lado do credenciado, em uma frase. */
function minhaParte(t: MeuTermoResumo): string {
  const minha = t.assinaturas.find((a) => a.papel === 'credenciado');
  if (!minha) return '—';
  return ROTULO_CONVITE[minha.status];
}

/**
 * Um Termo de Adesão em Contratos e Termos: número, situação, vigência, a
 * assinatura do credenciado e, abaixo, as Ordens de Serviço do termo.
 *
 * O cartão NÃO é alvo de clique. O caminho para o detalhe do termo é o link
 * no número; a sub-tabela leva ao detalhe de cada OS. Um cartão clicável com
 * links e linhas clicáveis por dentro aninharia controles (axe,
 * nested-interactive).
 */
export function TermoCard({ termo }: { termo: MeuTermoResumo }): ReactElement {
  const titulo = useId();
  const numero = numeroExibido(termo);

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="min-w-0 text-base font-semibold">
            <Link
              to={`/credenciado/contratos/${encodeURIComponent(termo.id)}`}
              className="text-link focus-visible:ring-ring rounded font-mono hover:underline focus:outline-hidden focus-visible:ring-2"
            >
              <span className="sr-only">Termo de Adesão nº </span>
              {numero}
            </Link>
          </h2>
          <StatusBadge
            status={VARIANTE_STATUS_TERMO[termo.status]}
            label={ROTULO_STATUS_TERMO[termo.status]}
          />
        </div>

        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          {/* `dt` e `dd` filhos diretos do `div`: o ícone fica dentro do `dt`. */}
          <div className="min-w-0">
            <dt className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <Calendar className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Vigência
            </dt>
            <dd className="font-medium">
              {dataLegivel(termo.dataInicio)} a {dataLegivel(termo.dataFim)}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <FileSignature className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Sua assinatura
            </dt>
            <dd className="font-medium">{minhaParte(termo)}</dd>
          </div>
        </dl>

        <section aria-labelledby={titulo} className="min-w-0 space-y-2">
          <h3 id={titulo} className="text-muted-foreground text-xs font-medium">
            Ordens de Serviço vinculadas
          </h3>
          <OsVinculadasDoTermo termoId={termo.id} rotulo={`Ordens de Serviço do termo ${numero}`} />
        </section>
      </CardContent>
    </Card>
  );
}

export default TermoCard;
